/**
 * useSearchActions
 *
 * Handles saving search results to a KB, saving search sessions to the database,
 * and loading saved search sessions into tabs.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import { searchService } from '../services/searchService';
import type { SearchType } from '../services/searchService';
import type { SearchResultsData, SearchResultDocument } from '../components/SearchResultsTab';
import { markSearchResultsSaved, markSearchSaved } from '../utils/searchResultsStorage';

export interface UseSearchActionsResult {
  handleSaveSearchResults: (tabId: string, selectedDocumentIds: string[]) => Promise<void>;
  handleSaveSearch: (tabId: string) => Promise<void>;
  handleSelectSearch: (searchIdOrTabId: string) => Promise<void>;
}

export function useSearchActions(
  entityId: string | undefined,
  selectedKbId: string | null,
  kbName: string,
  searchResultsTabs: SearchResultsData[],
  setSearchResultsTabs: React.Dispatch<React.SetStateAction<SearchResultsData[]>>,
  setKbsData: React.Dispatch<React.SetStateAction<unknown[]>>,
  setActiveTab: React.Dispatch<React.SetStateAction<string>>,
): UseSearchActionsResult {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const handleSaveSearchResults = useCallback(async (tabId: string, selectedDocumentIds: string[]) => {
    const tab = searchResultsTabs.find(t => t.tabId === tabId);
    if (!tab || !entityId) throw new Error('Tab or entity not found');

    // Use tab-level destination KB if set, otherwise fall back to globally selected KB
    const targetKbId = tab.destinationKbId || selectedKbId;
    const targetKbName = tab.destinationKbName || kbName || targetKbId;
    if (!targetKbId) throw new Error(t('searchResults.selectKb', 'Select a knowledge base in the Metadata tab.'));
    if (!selectedDocumentIds || selectedDocumentIds.length === 0) throw new Error(t('searchResults.selectDocument', 'Select at least one document.'));

    const existingDocs = await knowledgeBaseService.getDocuments(targetKbId);
    const existingIds = new Set(existingDocs.map(doc => (doc as unknown as Record<string, string>).document_id || (doc as unknown as Record<string, string>).id));
    const uniqueToAdd = selectedDocumentIds.filter(id => !existingIds.has(id));

    if (uniqueToAdd.length === 0) {
      markSearchResultsSaved(entityId, tabId);
      setSearchResultsTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, isSaved: true } : t));
      addToast({ type: 'info', title: t('searchResults.nothingToSave', 'Nothing to save'), message: t('searchResults.alreadyInKb', 'Selected documents are already in this knowledge base.') });
      return;
    }

    for (const docId of uniqueToAdd) {
      await knowledgeBaseService.addDocument(targetKbId, docId, 'medium');
    }

    // If this tab has a saved SearchRecord, update its saved_doc_ids
    if (tab.searchRecordId) {
      const allSavedIds = Array.from(new Set([...(tab.documents.filter(d => selectedDocumentIds.includes(d.document_id)).map(d => d.document_id))]));
      await searchService.updateSearchRecord(entityId, tab.searchRecordId, allSavedIds);
    }

    markSearchResultsSaved(entityId, tabId);
    setSearchResultsTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, isSaved: true } : t));
    setKbsData(prev => prev.map(kb => {
      const kbR = kb as Record<string, unknown>;
      if (kbR.id !== targetKbId) return kb;
      const currentCount = Number(kbR.document_count || 0);
      return { ...kbR, document_count: currentCount + uniqueToAdd.length };
    }));
    addToast({ type: 'success', title: t('searchResults.saved', 'Saved'), message: t('searchResults.savedToKb', 'Saved {{count}} document(s) to {{kbName}}', { count: uniqueToAdd.length, kbName: targetKbName }) });
  }, [searchResultsTabs, entityId, selectedKbId, kbName, setSearchResultsTabs, setKbsData, addToast, t]);

  const handleSaveSearch = useCallback(async (tabId: string) => {
    const tab = searchResultsTabs.find(t => t.tabId === tabId);
    if (!tab || !entityId) throw new Error('Tab or entity not found');

    const searchTypeLower = (tab.searchType || '').toLowerCase() as SearchType;
    const isSearchRecord = ['rags', 'dpds', 'ops_agent', 'ops_direct'].includes(searchTypeLower);

    if (isSearchRecord) {
      const record = await searchService.createSearchRecord(entityId, {
        sub_project_id: entityId,
        search_type: searchTypeLower,
        source_kb_id: tab.kbId || null,
        destination_kb_id: tab.destinationKbId || null,
        intent: null,
        query_text: tab.query || null,
        kw_query: tab.kwQuery || null,
        sm_query: tab.smQuery || null,
        result_doc_ids: tab.documents.map(doc => doc.document_id),
        params: {
          search_mode: tab.searchMode || 'hybrid',
          top_k: tab.topK ?? 50,
          top_n: tab.topN ?? 10,
          enable_reranking: tab.enableReranking !== false,
          selected_feature_ids: tab.selectedFeatureIds,
          ...tab.searchMetadata,
        },
      });
      markSearchSaved(entityId, tabId);
      setSearchResultsTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, isSearchSaved: true, searchRecordId: record.id } : t));
    } else {
      const searchData = {
        sub_project_id: entityId,
        kb_id: tab.kbId,
        query_text: tab.query,
        kw_query: tab.kwQuery,
        sm_query: tab.smQuery,
        search_mode: tab.searchMode || 'hybrid',
        top_k: tab.topK || 50,
        top_n: tab.topN || 10,
        enable_reranking: tab.enableReranking !== false,
        selected_feature_ids: tab.selectedFeatureIds,
        document_ids: tab.documents.map(doc => doc.document_id),
        search_metadata: tab.searchMetadata,
      };
      await searchService.saveRagSearchSession(entityId, searchData);
      markSearchSaved(entityId, tabId);
      setSearchResultsTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, isSearchSaved: true } : t));
    }
    addToast({ type: 'success', title: t('scv2.searchSavedSuccess', 'Search saved'), message: t('scv2.searchSavedSuccessMsg', 'Search session has been saved to the database.') });
  }, [searchResultsTabs, entityId, setSearchResultsTabs, addToast, t]);

  const handleSelectSearch = useCallback(async (searchIdOrTabId: string) => {
    try {
      // Check if already open
      const existingTab = searchResultsTabs.find(tab => tab.tabId === searchIdOrTabId);
      if (existingTab) { setActiveTab(existingTab.tabId); return; }

      const session = await searchService.getSession(searchIdOrTabId);
      const metadata = session.search_query || {};
      const searchMetadata: Record<string, unknown> = typeof metadata === 'object' ? metadata as unknown as Record<string, unknown> : {};

      let kbId = '';
      let kbNameForTab = '';
      if (session.search_service === 'rag' || session.search_service === 'RAG') {
        try {
          const queryData: Record<string, unknown> = JSON.parse(session.request || '{}');
          kbId = (queryData.kb_id as string) || (searchMetadata.kb_id as string) || '';
          kbNameForTab = (queryData.kb_name as string) || (searchMetadata.kb_name as string) || 'Unknown KB';
        } catch {
          kbId = (searchMetadata.kb_id as string) || '';
          kbNameForTab = (searchMetadata.kb_name as string) || 'Unknown KB';
        }
      }

      const documents: SearchResultDocument[] = (session.returned_documents || []).map((docId: string) => ({
        document_id: docId,
        document_title: docId.substring(0, 12) + '...',
        relevance_score: 0,
        kb_id: kbId,
        kb_name: kbNameForTab,
        chunks: [],
      }));

      const newTab: SearchResultsData = {
        tabId: session.id,
        tabName: `Docs (${session.session_name})`,
        documents,
        searchType: session.search_service.toUpperCase(),
        kbId,
        kbName: kbNameForTab,
        query: session.search_query?.query_string || '',
        kwQuery: searchMetadata.kw_query as string | undefined,
        smQuery: searchMetadata.sm_query as string | undefined,
        searchMode: (searchMetadata.search_mode as string) || 'hybrid',
        topK: (searchMetadata.top_k as number) || 50,
        topN: (searchMetadata.top_n as number) || 10,
        enableReranking: searchMetadata.enable_reranking !== false,
        selectedFeatureIds: (searchMetadata.selected_feature_ids as string[]) || [],
        searchMetadata,
        timestamp: session.created_date,
        isSaved: false,
        isSearchSaved: true,
      };

      setSearchResultsTabs(prev => [...prev, newTab]);
      setActiveTab(newTab.tabId);
    } catch (err) {
      console.error('Failed to load search session:', err);
      addToast({ type: 'error', title: t('scv2.searchLoadFailed', 'Failed to load search'), message: err instanceof Error ? err.message : 'Unknown error' });
    }
  }, [searchResultsTabs, setSearchResultsTabs, setActiveTab, addToast, t]);

  return { handleSaveSearchResults, handleSaveSearch, handleSelectSearch };
}
